import React, { Suspense, lazy, useEffect, useState, useCallback } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/lib/i18n";
import { sanitizeTab } from "@/lib/security";
import { getCsrfToken } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const EnterpriseView = lazy(() => import("@/components/EnterpriseView"));
const SeguraHubView = lazy(() => import("@/components/SeguraHubView"));
const ContactView = lazy(() => import("@/components/ContactView"));
const LegalView = lazy(() => import("@/components/LegalView"));

function Loader() {
  return (
    <div className="flex items-center justify-center py-32" data-testid="view-loader">
      <div
        className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
        style={{ borderColor: "#3a1f9c", borderTopColor: "transparent" }}
        role="status"
        aria-label="Cargando"
      />
    </div>
  );
}

function Shell() {
  const [activeTab, setActiveTab] = useState("enterprise");

  // Inject security meta tags (CSP, frame-options, content-type-options).
  useEffect(() => {
    injectSecurityMeta();
    // Pre-warm CSRF token at boot.
    getCsrfToken().catch(() => {});
  }, []);

  // Scroll to top on tab change.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const onTabChange = useCallback((next, anchorId) => {
    setActiveTab(sanitizeTab(next));
    if (anchorId) {
      // Allow the new view to mount before scrolling to the requested anchor.
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.getElementById(anchorId);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
      });
    }
  }, []);

  return (
    <div className="App min-h-screen flex flex-col">
      <Header activeTab={activeTab} onTabChange={onTabChange} />
      <main className="flex-1">
        <Suspense fallback={<Loader />}>
          {activeTab === "enterprise" && <EnterpriseView onTabChange={onTabChange} />}
          {activeTab === "segura" && <SeguraHubView onTabChange={onTabChange} />}
          {activeTab === "contact" && <ContactView />}
          {activeTab === "legal" && <LegalView />}
        </Suspense>
      </main>
      <Footer onTabChange={onTabChange} />
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

function injectSecurityMeta() {
  const metas = [
    {
      httpEquiv: "Content-Security-Policy",
      content:
        "default-src 'self'; img-src 'self' data: https:; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com data:; script-src 'self' 'unsafe-inline' https://assets.emergent.sh https://*.posthog.com https://*.i.posthog.com; connect-src 'self' https: wss: https://*.posthog.com https://*.i.posthog.com; frame-ancestors 'none'; base-uri 'self'; form-action 'self';",
    },
    { httpEquiv: "X-Content-Type-Options", content: "nosniff" },
    { httpEquiv: "X-Frame-Options", content: "DENY" },
    { httpEquiv: "Referrer-Policy", content: "strict-origin-when-cross-origin" },
    { httpEquiv: "Strict-Transport-Security", content: "max-age=63072000; includeSubDomains" },
  ];
  metas.forEach((m) => {
    if (document.querySelector(`meta[http-equiv="${m.httpEquiv}"]`)) return;
    const el = document.createElement("meta");
    el.setAttribute("http-equiv", m.httpEquiv);
    el.setAttribute("content", m.content);
    document.head.appendChild(el);
  });
}

export default function App() {
  return (
    <LanguageProvider>
      <Shell />
    </LanguageProvider>
  );
}
